import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, List, ListItem, Grid,
  Card, CardHeader, CardContent, 
  TextField, InputAdornment, Button, Dialog, 
  DialogTitle, DialogContent, DialogActions, FormControl, 
  InputLabel, Select, MenuItem, Collapse, Chip, 
  IconButton, Avatar, Badge, Divider, Paper, Stepper,
  Step, StepLabel, StepContent, Tooltip, TableContainer,
  Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, LinearProgress
} from '@mui/material';

import {
  Search as SearchIcon,
  Add as AddIcon,
  Info as InfoOutlinedIcon,
  Delete as DeleteIcon,
  AssignmentInd as AssignmentIndIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Pending as PendingIcon,
  Flag as FlagIcon,
  PersonAdd as PersonAddIcon,
  DonutSmall as DonutSmallIcon,
  Comment as CommentIcon,
  Update as UpdateIcon,
  History as HistoryIcon,
  Edit as EditIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  FilterAlt as FilterListIcon,
  Sort as SortIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';

import { TICKET_CATEGORIES, TICKET_STATUSES, TICKET_PRIORITIES } from '../utils/constants';

const TicketsTab = ({
  visible,
  activeTicket,
  tickets = [],
  setTickets,
  onSelectTicket,
  theme,
  isMobile,
  onBackToTickets,
  onViewDetails,
  expandedTicket,
  setExpandedTicket
}) => {
  // State variables
  const [ticketFormOpen, setTicketFormOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium'
  });
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState(['all']);
  const [selectedPriorities, setSelectedPriorities] = useState(['all']);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionTicket, setActionTicket] = useState(null);
  const [actionComment, setActionComment] = useState('');
  const [actionType, setActionType] = useState('');
  const [actionAssignee, setActionAssignee] = useState('');

  // Handle creating a new ticket
  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.description) {
      return;
    }

    setLoading(true);
    try {
      // Simulation of API call
      const newTicketData = {
        ...newTicket,
        id: `TKT-${Math.floor(1000 + Math.random() * 9000)}`,
        status: 'open',
        customer: {
          id: 'user-self',
          name: 'You',
          email: 'you@example.com',
        },
        createdAt: new Date().toISOString(),
        lastActivity: new Date().toISOString(),
        messages: [
          {
            id: `msg-${Math.random().toString(36).substring(2, 10)}`,
            senderId: 'user-self',
            senderName: 'You',
            content: newTicket.description,
            timestamp: new Date().toISOString(),
            read: true
          }
        ],
        unread: false,
        actions: [
          {
            id: `act-${Math.random().toString(36).substring(2, 10)}`,
            type: 'create',
            timestamp: new Date().toISOString(),
            user: 'System',
            details: 'Ticket created'
          }
        ]
      };

      setTickets(prev => [newTicketData, ...prev]);
      
      setNewTicket({
        subject: '',
        description: '',
        category: 'general',
        priority: 'medium'
      });
      setTicketFormOpen(false);
      
    } catch (error) {
      console.error('Error creating ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle category filter change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
  };

  // Handle ticket form open/close
  const handleTicketFormOpen = () => {
    setTicketFormOpen(true);
  };

  const handleTicketFormClose = () => {
    setTicketFormOpen(false);
  };

  // Handle toggling ticket details
  const handleToggleTicketDetails = (ticketId) => {
    if (typeof setExpandedTicket === 'function') {
      setExpandedTicket(ticketId === expandedTicket ? null : ticketId);
    }
  };

  // Handle status filter
  const handleStatusFilterChange = (event, status) => {
    if (status === 'all') {
      setSelectedStatuses(['all']);
    } else {
      const newSelectedStatuses = selectedStatuses.includes('all')
        ? [status]
        : selectedStatuses.includes(status)
          ? selectedStatuses.filter(s => s !== status)
          : [...selectedStatuses, status];
      
      setSelectedStatuses(newSelectedStatuses.length ? newSelectedStatuses : ['all']);
    }
  };

  // Handle priority filter
  const handlePriorityFilterChange = (event, priority) => {
    if (priority === 'all') {
      setSelectedPriorities(['all']);
    } else {
      const newSelectedPriorities = selectedPriorities.includes('all')
        ? [priority]
        : selectedPriorities.includes(priority)
          ? selectedPriorities.filter(p => p !== priority)
          : [...selectedPriorities, priority];
      
      setSelectedPriorities(newSelectedPriorities.length ? newSelectedPriorities : ['all']);
    }
  };

  // Handle filter menu
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
    setStatusFilterOpen(true);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setStatusFilterOpen(false);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle action dialog
  const handleActionDialogOpen = (ticket, type) => {
    setActionTicket(ticket);
    setActionType(type);
    setActionComment('');
    setActionAssignee('');
    setActionDialogOpen(true);
  };

  const handleActionDialogClose = () => {
    setActionDialogOpen(false);
    setActionTicket(null);
    setActionType('');
  };

  const handleActionSubmit = () => {
    if (!actionTicket) return;

    const updatedTickets = tickets.map(ticket => {
      if (ticket.id === actionTicket.id) {
        const updatedTicket = { ...ticket };
        
        // Add action to history
        const newAction = {
          id: `act-${Math.random().toString(36).substring(2, 10)}`,
          type: actionType,
          timestamp: new Date().toISOString(),
          user: 'Agent Name', // Would come from logged in user
          details: actionComment || `Ticket ${actionType}`
        };
        
        updatedTicket.actions = [...(updatedTicket.actions || []), newAction];
        
        // Update ticket based on action type
        switch(actionType) {
          case 'status':
            updatedTicket.status = actionComment;
            updatedTicket.lastActivity = new Date().toISOString();
            break;
          case 'comment':
            updatedTicket.messages = [
              ...(updatedTicket.messages || []), 
              {
                id: `msg-${Math.random().toString(36).substring(2, 10)}`,
                senderId: 'agent-self',
                senderName: 'Agent',
                content: actionComment,
                timestamp: new Date().toISOString(),
                isInternal: true
              }
            ];
            updatedTicket.lastActivity = new Date().toISOString();
            break;
          case 'assign':
            updatedTicket.assignedTo = actionAssignee;
            updatedTicket.lastActivity = new Date().toISOString();
            break;
          case 'priority':
            updatedTicket.priority = actionComment;
            updatedTicket.lastActivity = new Date().toISOString();
            break;
          default:
            break;
        }
        
        return updatedTicket;
      }
      return ticket;
    });
    
    setTickets(updatedTickets);
    handleActionDialogClose();
  };

  // Filter tickets based on search, category, status, and priority
  const filteredTickets = tickets.filter(ticket => {
    // Filter by category
    if (activeCategory !== 'all' && ticket.category !== activeCategory) {
      return false;
    }
    
    // Filter by status
    if (!selectedStatuses.includes('all') && !selectedStatuses.includes(ticket.status)) {
      return false;
    }
    
    // Filter by priority
    if (!selectedPriorities.includes('all') && !selectedPriorities.includes(ticket.priority)) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return ticket.subject?.toLowerCase().includes(query) || 
             ticket.description?.toLowerCase().includes(query) ||
             ticket.id?.toLowerCase().includes(query) ||
             ticket.customer?.name?.toLowerCase().includes(query) ||
             ticket.customer?.email?.toLowerCase().includes(query);
    }
    
    return true;
  });

  // Calculate ticket statistics
  const ticketStats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    pending: tickets.filter(t => t.status === 'pending').length,
    urgent: tickets.filter(t => t.status === 'urgent').length,
    closed: tickets.filter(t => t.status === 'closed').length,
    unassigned: tickets.filter(t => !t.assignedTo).length,
  };

  // Status icon component
  const StatusIcon = ({ status }) => {
    switch(status) {
      case 'open':
        return <OpenStatusIcon color={TICKET_STATUSES.open.color} />;
      case 'pending':
        return <PendingIcon sx={{ color: TICKET_STATUSES.pending.color }} />;
      case 'urgent':
        return <ErrorOutlineIcon sx={{ color: TICKET_STATUSES.urgent.color }} />;
      case 'closed':
        return <CheckCircleIcon sx={{ color: TICKET_STATUSES.closed.color }} />;
      default:
        return null;
    }
  };

  const OpenStatusIcon = ({ color }) => (
    <DonutSmallIcon sx={{ color }} />
  );

  // Format date helper function
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get action icon based on type
  const getActionIcon = (actionType) => {
    switch(actionType) {
      case 'create':
        return <AddIcon fontSize="small" />;
      case 'status':
        return <UpdateIcon fontSize="small" />;
      case 'comment':
        return <CommentIcon fontSize="small" />;
      case 'assign':
        return <PersonAddIcon fontSize="small" />;
      case 'priority':
        return <FlagIcon fontSize="small" />;
      default:
        return <HistoryIcon fontSize="small" />;
    }
  };

  // Early return if not visible
  if (!visible) return null;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header section with stats */}
      <Box sx={{ 
        p: 2, 
        backgroundColor: 'rgba(30, 40, 55, 0.8)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'white' }}>Ticket Management</Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleTicketFormOpen}
            sx={{ 
              bgcolor: theme.palette.primary.main,
              '&:hover': { bgcolor: theme.palette.primary.dark }
            }}
          >
            New Ticket
          </Button>
        </Box>
        
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Paper 
              sx={{ 
                p: 1.5, 
                borderRadius: 1, 
                bgcolor: 'rgba(255, 255, 255, 0.05)', 
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center'
              }}
            >
              <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)" gutterBottom>
                Total
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {ticketStats.total}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2}>
            <Paper 
              sx={{ 
                p: 1.5, 
                borderRadius: 1, 
                bgcolor: `${TICKET_STATUSES.open.color}15`, 
                color: 'white',
                border: `1px solid ${TICKET_STATUSES.open.color}30`,
                textAlign: 'center'
              }}
            >
              <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)" gutterBottom>
                Open
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: TICKET_STATUSES.open.color }}>
                {ticketStats.open}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2}>
            <Paper 
              sx={{ 
                p: 1.5, 
                borderRadius: 1, 
                bgcolor: `${TICKET_STATUSES.pending.color}15`, 
                color: 'white',
                border: `1px solid ${TICKET_STATUSES.pending.color}30`,
                textAlign: 'center'
              }}
            >
              <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: TICKET_STATUSES.pending.color }}>
                {ticketStats.pending}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2}>
            <Paper 
              sx={{ 
                p: 1.5, 
                borderRadius: 1, 
                bgcolor: `${TICKET_STATUSES.urgent.color}15`, 
                color: 'white',
                border: `1px solid ${TICKET_STATUSES.urgent.color}30`,
                textAlign: 'center'
              }}
            >
              <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)" gutterBottom>
                Urgent
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: TICKET_STATUSES.urgent.color }}>
                {ticketStats.urgent}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2}>
            <Paper 
              sx={{ 
                p: 1.5, 
                borderRadius: 1, 
                bgcolor: `${TICKET_STATUSES.closed.color}15`, 
                color: 'white',
                border: `1px solid ${TICKET_STATUSES.closed.color}30`,
                textAlign: 'center'
              }}
            >
              <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)" gutterBottom>
                Closed
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: TICKET_STATUSES.closed.color }}>
                {ticketStats.closed}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2}>
            <Paper 
              sx={{ 
                p: 1.5, 
                borderRadius: 1, 
                bgcolor: 'rgba(156, 39, 176, 0.1)', 
                color: 'white',
                border: '1px solid rgba(156, 39, 176, 0.3)',
                textAlign: 'center'
              }}
            >
              <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)" gutterBottom>
                Unassigned
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#9c27b0' }}>
                {ticketStats.unassigned}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        
        {/* Search and filters */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search tickets..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: { xs: 1, md: 'none' },
              minWidth: { xs: '100%', sm: 250 },
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(0, 0, 0, 0.2)',
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255, 255, 255, 0.5)',
                opacity: 1
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                </InputAdornment>
              )
            }}
          />
          
          <Button 
            variant="outlined" 
            startIcon={<FilterListIcon />}
            onClick={handleFilterClick}
            sx={{ 
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': { borderColor: 'rgba(255, 255, 255, 0.5)', bgcolor: 'rgba(255, 255, 255, 0.05)' }
            }}
          >
            Filter
          </Button>
          
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label="All Categories" 
              size="small"
              onClick={() => handleCategoryChange('all')}
              sx={{ 
                bgcolor: activeCategory === 'all' ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '&:hover': { bgcolor: activeCategory === 'all' ? theme.palette.primary.dark : 'rgba(255, 255, 255, 0.2)' }
              }}
            />
            {TICKET_CATEGORIES.map(category => (
              <Chip 
                key={category.id}
                label={category.label} 
                size="small"
                onClick={() => handleCategoryChange(category.id)}
                sx={{ 
                  bgcolor: activeCategory === category.id ? category.color : 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  '&:hover': { bgcolor: activeCategory === category.id ? category.color : 'rgba(255, 255, 255, 0.2)' }
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Status filter dialog */}
      <Dialog 
        open={statusFilterOpen} 
        onClose={handleFilterClose}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(30, 35, 45, 0.95)',
            color: 'white',
            minWidth: 300,
            backdropFilter: 'blur(5px)'
          }
        }}
      >
        <DialogTitle>Filter Tickets</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Status</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip 
              label="All" 
              size="small"
              color={selectedStatuses.includes('all') ? 'primary' : 'default'}
              variant={selectedStatuses.includes('all') ? "filled" : "outlined"}
              onClick={(e) => handleStatusFilterChange(e, 'all')}
            />
            {Object.entries(TICKET_STATUSES).map(([key, { label, color }]) => (
              <Chip 
                key={key}
                label={label} 
                size="small"
                color="default"
                variant={selectedStatuses.includes(key) && !selectedStatuses.includes('all') ? "filled" : "outlined"}
                onClick={(e) => handleStatusFilterChange(e, key)}
                sx={{ 
                  color: color,
                  borderColor: color,
                  bgcolor: selectedStatuses.includes(key) && !selectedStatuses.includes('all') ? `${color}30` : 'transparent',
                }}
              />
            ))}
          </Box>
          
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Priority</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label="All" 
              size="small"
              color={selectedPriorities.includes('all') ? 'primary' : 'default'}
              variant={selectedPriorities.includes('all') ? "filled" : "outlined"}
              onClick={(e) => handlePriorityFilterChange(e, 'all')}
            />
            {Object.entries(TICKET_PRIORITIES).map(([key, { label, color }]) => (
              <Chip 
                key={key}
                label={label} 
                size="small"
                color="default"
                variant={selectedPriorities.includes(key) && !selectedPriorities.includes('all') ? "filled" : "outlined"}
                onClick={(e) => handlePriorityFilterChange(e, key)}
                sx={{ 
                  color: color,
                  borderColor: color,
                  bgcolor: selectedPriorities.includes(key) && !selectedPriorities.includes('all') ? `${color}30` : 'transparent',
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFilterClose} sx={{ color: 'white' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Main content - Tickets Table */}
      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'rgba(18, 18, 24, 0.5)' }}>
        <TableContainer>
          <Table stickyHeader sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: 'rgba(30, 40, 55, 0.95)', color: 'rgba(255, 255, 255, 0.7)' }}>ID</TableCell>
                <TableCell sx={{ bgcolor: 'rgba(30, 40, 55, 0.95)', color: 'rgba(255, 255, 255, 0.7)' }}>Subject</TableCell>
                <TableCell sx={{ bgcolor: 'rgba(30, 40, 55, 0.95)', color: 'rgba(255, 255, 255, 0.7)' }}>Customer</TableCell>
                <TableCell sx={{ bgcolor: 'rgba(30, 40, 55, 0.95)', color: 'rgba(255, 255, 255, 0.7)' }}>Status</TableCell>
                <TableCell sx={{ bgcolor: 'rgba(30, 40, 55, 0.95)', color: 'rgba(255, 255, 255, 0.7)' }}>Last Updated</TableCell>
                <TableCell sx={{ bgcolor: 'rgba(30, 40, 55, 0.95)', color: 'rgba(255, 255, 255, 0.7)' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTickets.length > 0 ? (
                filteredTickets
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((ticket) => (
                    <React.Fragment key={ticket.id}>
                      <TableRow
                        hover
                        sx={{ 
                          cursor: 'pointer',
                          bgcolor: ticket.unread ? 'rgba(76, 133, 240, 0.1)' : 'transparent',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                        }}
                      >
                        <TableCell 
                          sx={{ 
                            color: 'white', 
                            borderBottomColor: 'rgba(255, 255, 255, 0.1)',
                            borderLeft: expandedTicket === ticket.id ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent'
                          }}
                        >
                          {ticket.id}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            color: 'white', 
                            borderBottomColor: 'rgba(255, 255, 255, 0.1)',
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          onClick={() => onSelectTicket(ticket.id)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {ticket.unread && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: theme.palette.primary.main,
                                  mr: 1
                                }}
                              />
                            )}
                            <Typography noWrap>{ticket.subject}</Typography>
                          </Box>
                          {ticket.priority && (
                            <Chip 
                              size="small" 
                              label={TICKET_PRIORITIES[ticket.priority]?.label || ticket.priority} 
                              sx={{ 
                                height: 18,
                                fontSize: '0.65rem',
                                color: TICKET_PRIORITIES[ticket.priority]?.color || '#999',
                                bgcolor: `${TICKET_PRIORITIES[ticket.priority]?.color || '#999'}15`,
                                mt: 0.5
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'white', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                              src={ticket.customer?.avatar} 
                              alt={ticket.customer?.name} 
                              sx={{ width: 24, height: 24, mr: 1, fontSize: '0.75rem' }}
                            >
                              {ticket.customer?.name?.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" noWrap>
                              {ticket.customer?.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'white', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>
                          <Chip 
                            icon={<StatusIcon status={ticket.status} />}
                            label={TICKET_STATUSES[ticket.status]?.label || ticket.status}
                            size="small"
                            sx={{ 
                              bgcolor: `${TICKET_STATUSES[ticket.status]?.color || '#999'}20`,
                              color: TICKET_STATUSES[ticket.status]?.color || '#999',
                              height: 24,
                              '& .MuiChip-icon': {
                                color: 'inherit'
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'white', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>
                          {formatDate(ticket.lastActivity)}
                        </TableCell>
                        <TableCell sx={{ color: 'white', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleTicketDetails(ticket.id);
                                }}
                                sx={{ 
                                  color: 'rgba(255,255,255,0.7)',
                                  backgroundColor: expandedTicket === ticket.id ? 'rgba(255,255,255,0.1)' : 'transparent',
                                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                                }}
                              >
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Update Status">
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleActionDialogOpen(ticket, 'status');
                                }}
                                sx={{ 
                                  color: 'rgba(255,255,255,0.7)',
                                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                                }}
                              >
                                <UpdateIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Add Comment">
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleActionDialogOpen(ticket, 'comment');
                                }}
                                sx={{ 
                                  color: 'rgba(255,255,255,0.7)',
                                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                                }}
                              >
                                <CommentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Assign Ticket">
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleActionDialogOpen(ticket, 'assign');
                                }}
                                sx={{ 
                                  color: 'rgba(255,255,255,0.7)',
                                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                                }}
                              >
                                <AssignmentIndIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded ticket details row */}
                      {expandedTicket === ticket.id && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ p: 0, borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>
                            <Box 
                              sx={{ 
                                p: 3, 
                                bgcolor: 'rgba(30, 40, 55, 0.5)',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                              }}
                            >
                              <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                                    Ticket Details
                                  </Typography>
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" component="span">
                                      Created:
                                    </Typography>
                                    <Typography variant="body2" color="white" component="span" sx={{ ml: 1 }}>
                                      {new Date(ticket.createdAt).toLocaleString()}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" component="span">
                                      Category:
                                    </Typography>
                                    <Typography variant="body2" color="white" component="span" sx={{ ml: 1 }}>
                                      {TICKET_CATEGORIES.find(c => c.id === ticket.category)?.label || 'Unknown'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" component="span">
                                      Priority:
                                    </Typography>
                                    <Chip 
                                      size="small" 
                                      label={TICKET_PRIORITIES[ticket.priority]?.label || ticket.priority} 
                                      sx={{ 
                                        ml: 1,
                                        height: 20,
                                        fontSize: '0.7rem',
                                        color: TICKET_PRIORITIES[ticket.priority]?.color || '#999',
                                        bgcolor: `${TICKET_PRIORITIES[ticket.priority]?.color || '#999'}15`
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" component="span">
                                      Status:
                                    </Typography>
                                    <Chip 
                                      size="small" 
                                      icon={<StatusIcon status={ticket.status} />}
                                      label={TICKET_STATUSES[ticket.status]?.label || ticket.status}
                                      sx={{ 
                                        ml: 1,
                                        height: 20,
                                        fontSize: '0.7rem',
                                        color: TICKET_STATUSES[ticket.status]?.color || '#999',
                                        bgcolor: `${TICKET_STATUSES[ticket.status]?.color || '#999'}15`,
                                        '& .MuiChip-icon': {
                                          color: 'inherit'
                                        }
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" component="span">
                                      Customer:
                                    </Typography>
                                    <Typography variant="body2" color="white" component="span" sx={{ ml: 1 }}>
                                      {ticket.customer?.name} ({ticket.customer?.email})
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" gutterBottom>
                                      Description:
                                    </Typography>
                                    <Typography variant="body2" color="white" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {ticket.description || ticket.messages?.[0]?.content || 'No description available'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                  <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                                    Action History
                                  </Typography>
                                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                                    <Stepper orientation="vertical" sx={{ 
                                      '& .MuiStepLabel-label': { color: 'white' },
                                      '& .MuiStepContent-root': { borderColor: 'rgba(255, 255, 255, 0.2)' }
                                    }}>
                                      {[...(ticket.actions || [])].reverse().map((action, index) => (
                                        <Step key={action.id || index} active={true} completed={true}>
                                          <StepLabel 
                                            StepIconComponent={() => (
                                              <Box sx={{ 
                                                width: 24, 
                                                height: 24, 
                                                borderRadius: '50%',
                                                bgcolor: 'rgba(255, 255, 255, 0.1)', 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center'
                                              }}>
                                                {getActionIcon(action.type)}
                                              </Box>
                                            )}
                                          >
                                            <Typography variant="body2" color="white">
                                              {action.type === 'status' ? 'Status updated' : 
                                               action.type === 'comment' ? 'Comment added' : 
                                               action.type === 'assign' ? 'Ticket assigned' : 
                                               action.type === 'create' ? 'Ticket created' : 
                                               action.type === 'priority' ? 'Priority changed' : 
                                               'Action taken'}
                                            </Typography>
                                            <Typography variant="caption" color="rgba(255, 255, 255, 0.6)">
                                              {action.user} • {formatDate(action.timestamp)}
                                            </Typography>
                                          </StepLabel>
                                          <StepContent>
                                            <Typography variant="body2" color="rgba(255, 255, 255, 0.9)">
                                              {action.details}
                                            </Typography>
                                          </StepContent>
                                        </Step>
                                      ))}
                                    </Stepper>
                                    
                                    {(!ticket.actions || ticket.actions.length === 0) && (
                                      <Typography variant="body2" color="rgba(255, 255, 255, 0.5)" sx={{ textAlign: 'center', py: 2 }}>
                                        No action history available
                                      </Typography>
                                    )}
                                  </Box>
                                </Grid>
                              </Grid>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<MoreVertIcon />}
                                  onClick={() => onSelectTicket(ticket.id)}
                                  sx={{ 
                                    color: 'white', 
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255, 255, 255, 0.05)' }
                                  }}
                                >
                                  View Full Details
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<CheckCircleOutlineIcon />}
                                  onClick={() => {
                                    handleActionDialogOpen(ticket, 'status');
                                    setActionComment('closed');
                                  }}
                                  color="primary"
                                  disabled={ticket.status === 'closed'}
                                >
                                  {ticket.status === 'closed' ? 'Already Closed' : 'Close Ticket'}
                                </Button>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', py: 4 }}>
                    {searchQuery || !selectedStatuses.includes('all') || !selectedPriorities.includes('all') || activeCategory !== 'all'
                      ? 'No tickets matching your filters'
                      : 'No tickets available'}
                  </TableCell>
                </TableRow>
              )}
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0 }}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredTickets.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            '.MuiTablePagination-selectIcon': { color: 'rgba(255, 255, 255, 0.7)' },
            '.MuiTablePagination-displayedRows': { color: 'rgba(255, 255, 255, 0.7)' },
            '.MuiTablePagination-selectLabel': { color: 'rgba(255, 255, 255, 0.7)' },
            '.MuiTablePagination-actions': {
              '& .MuiIconButton-root': {
                color: 'rgba(255, 255, 255, 0.7)'
              },
              '& .Mui-disabled': {
                color: 'rgba(255, 255, 255, 0.3)'
              }
            }
          }}
        />
      </Box>
      
      {/* Ticket form dialog */}
      <Dialog open={ticketFormOpen} onClose={handleTicketFormClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Ticket</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Subject"
            type="text"
            fullWidth
            value={newTicket.subject}
            onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
          />
          <TextField
            margin="dense"
            label="Description"
            multiline
            rows={4}
            fullWidth
            value={newTicket.description}
            onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Category</InputLabel>
            <Select
              value={newTicket.category}
              onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
              label="Category"
            >
              {TICKET_CATEGORIES.map(category => (
                <MenuItem key={category.id} value={category.id}>{category.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Priority</InputLabel>
            <Select
              value={newTicket.priority}
              onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
              label="Priority"
            >
              {Object.entries(TICKET_PRIORITIES).map(([key, { label }]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTicketFormClose}>Cancel</Button>
          <Button 
            onClick={handleCreateTicket} 
            variant="contained" 
            color="primary"
            disabled={loading || !newTicket.subject || !newTicket.description}
          >
            {loading ? 'Creating...' : 'Create Ticket'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Action dialog (status change, comments, assignment) */}
      <Dialog open={actionDialogOpen} onClose={handleActionDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'status' && 'Update Ticket Status'}
          {actionType === 'comment' && 'Add Comment to Ticket'}
          {actionType === 'assign' && 'Assign Ticket'}
          {actionType === 'priority' && 'Change Priority'}
        </DialogTitle>
        <DialogContent>
          {actionType === 'status' && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Status</InputLabel>
              <Select
                value={actionComment}
                onChange={(e) => setActionComment(e.target.value)}
                label="Status"
              >
                {Object.entries(TICKET_STATUSES).map(([key, { label }]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          {actionType === 'comment' && (
            <TextField
              autoFocus
              margin="dense"
              label="Comment"
              multiline
              rows={4}
              fullWidth
              value={actionComment}
              onChange={(e) => setActionComment(e.target.value)}
              placeholder="Add internal note or comment about this ticket"
            />
          )}
          
          {actionType === 'assign' && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Assign To</InputLabel>
              <Select
                value={actionAssignee}
                onChange={(e) => setActionAssignee(e.target.value)}
                label="Assign To"
              >
                <MenuItem value="agent1">John Doe</MenuItem>
                <MenuItem value="agent2">Jane Smith</MenuItem>
                <MenuItem value="agent3">Alex Johnson</MenuItem>
                <MenuItem value="unassigned">Unassigned</MenuItem>
              </Select>
            </FormControl>
          )}
          
          {actionType === 'priority' && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Priority</InputLabel>
              <Select
                value={actionComment}
                onChange={(e) => setActionComment(e.target.value)}
                label="Priority"
              >
                {Object.entries(TICKET_PRIORITIES).map(([key, { label }]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleActionDialogClose}>Cancel</Button>
          <Button 
            onClick={handleActionSubmit} 
            variant="contained" 
            color="primary"
            disabled={(actionType === 'comment' && !actionComment) || (actionType === 'assign' && !actionAssignee)}
          >
            {actionType === 'status' && 'Update Status'}
            {actionType === 'comment' && 'Add Comment'}
            {actionType === 'assign' && 'Assign Ticket'}
            {actionType === 'priority' && 'Update Priority'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TicketsTab;
